/**
 * TanStack Query hooks for the admin attribute-fields (PIM Field Library)
 * surface — PIM Wave P1 Task 2. Mirrors the `categories/hooks.ts` /
 * `products/hooks.ts` idiom exactly (query-key prefixes, invalidate-on-
 * success mutations, `useInfiniteQuery` for the keyset field list).
 *
 * Invalidation rules (brief-mandated):
 *  - field mutations (create/patch/archive/unarchive/delete) -> fields
 *    lists + that field's detail (archive/unarchive invalidate the same as
 *    patch).
 *  - option mutations (create/patch/archive/unarchive) -> that field's
 *    options + the fields list.
 *  - group mutations (create/patch/delete) -> groups + the fields list
 *    (fields carry `group_id`).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  archiveAttributeField,
  archiveAttributeFieldOption,
  attributeFieldGroupKeys,
  attributeFieldKeys,
  createAttributeField,
  createAttributeFieldGroup,
  createAttributeFieldOption,
  deleteAttributeField,
  deleteAttributeFieldGroup,
  getAttributeField,
  listAllAttributeFields,
  listAttributeFieldGroups,
  listAttributeFieldOptions,
  listAttributeFields,
  unarchiveAttributeField,
  unarchiveAttributeFieldOption,
  updateAttributeField,
  updateAttributeFieldGroup,
  updateAttributeFieldOption,
  type AttributeField,
  type AttributeFieldCreate,
  type AttributeFieldFilters,
  type AttributeFieldGroup,
  type AttributeFieldGroupCreate,
  type AttributeFieldGroupListResponse,
  type AttributeFieldGroupUpdate,
  type AttributeFieldListResponse,
  type AttributeFieldOption,
  type AttributeFieldOptionCreate,
  type AttributeFieldOptionListResponse,
  type AttributeFieldOptionUpdate,
  type AttributeFieldUpdate,
} from "@/features/admin/attribute-fields/api";

/** Mirrors the backend's own `DEFAULT_LIST_LIMIT` (`catalog_schema/repository.py`). */
const PAGE_LIMIT = 25;

export const DEFAULT_ATTRIBUTE_FIELD_FILTERS: AttributeFieldFilters = {
  fieldType: null,
  groupId: null,
  isArchived: null,
  q: null,
};

// ---------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------

export function useAttributeFields(
  filters: AttributeFieldFilters = DEFAULT_ATTRIBUTE_FIELD_FILTERS,
): UseInfiniteQueryResult<InfiniteData<AttributeFieldListResponse>, Error> {
  return useInfiniteQuery<
    AttributeFieldListResponse,
    Error,
    InfiniteData<AttributeFieldListResponse>,
    ReturnType<typeof attributeFieldKeys.list>,
    string | null
  >({
    queryKey: attributeFieldKeys.list(filters),
    queryFn: ({ pageParam }) =>
      listAttributeFields({
        cursor: pageParam,
        limit: PAGE_LIMIT,
        field_type: filters.fieldType,
        group_id: filters.groupId,
        is_archived: filters.isArchived,
        q: filters.q,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

export function useAttributeField(
  id: string | null,
): UseQueryResult<AttributeField, Error> {
  return useQuery<AttributeField, Error>({
    queryKey: attributeFieldKeys.detail(id ?? ""),
    queryFn: () => getAttributeField(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

/**
 * Full-drain field catalogue (PIM Wave P1 Task 8 additive — mirrors
 * `useAllAdminCategories`). Consumed by `category-bindings/`: the binding
 * row name/slug/type lookups and the add-field picker both need the whole
 * field set (including archived, so archived-but-bound fields still render
 * their name), not a paginated slice.
 */
export function useAllAttributeFields(): UseQueryResult<AttributeField[], Error> {
  return useQuery<AttributeField[], Error>({
    queryKey: attributeFieldKeys.all(),
    queryFn: () => listAllAttributeFields(),
    staleTime: 10_000,
  });
}

function useInvalidateAfterFieldWrite(): (id?: string) => Promise<void> {
  const qc = useQueryClient();
  return async (id?: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "attribute-fields", "list"] }),
      ...(id
        ? [qc.invalidateQueries({ queryKey: attributeFieldKeys.detail(id) })]
        : []),
    ]);
  };
}

export function useCreateAttributeField(): UseMutationResult<
  AttributeField,
  Error,
  AttributeFieldCreate
> {
  const invalidate = useInvalidateAfterFieldWrite();
  return useMutation<AttributeField, Error, AttributeFieldCreate>({
    mutationFn: (body) => createAttributeField(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAttributeField(): UseMutationResult<
  AttributeField,
  Error,
  { id: string; body: AttributeFieldUpdate }
> {
  const invalidate = useInvalidateAfterFieldWrite();
  return useMutation<
    AttributeField,
    Error,
    { id: string; body: AttributeFieldUpdate }
  >({
    mutationFn: ({ id, body }) => updateAttributeField(id, body),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}

export function useArchiveAttributeField(): UseMutationResult<
  AttributeField,
  Error,
  string
> {
  const invalidate = useInvalidateAfterFieldWrite();
  return useMutation<AttributeField, Error, string>({
    mutationFn: (id) => archiveAttributeField(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function useUnarchiveAttributeField(): UseMutationResult<
  AttributeField,
  Error,
  string
> {
  const invalidate = useInvalidateAfterFieldWrite();
  return useMutation<AttributeField, Error, string>({
    mutationFn: (id) => unarchiveAttributeField(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function useDeleteAttributeField(): UseMutationResult<
  AttributeField,
  Error,
  string
> {
  const invalidate = useInvalidateAfterFieldWrite();
  return useMutation<AttributeField, Error, string>({
    mutationFn: (id) => deleteAttributeField(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

// ---------------------------------------------------------------------
// Field options (nested under a field)
// ---------------------------------------------------------------------

export function useAttributeFieldOptions(
  fieldId: string | null,
): UseQueryResult<AttributeFieldOptionListResponse, Error> {
  return useQuery<AttributeFieldOptionListResponse, Error>({
    queryKey: attributeFieldKeys.options(fieldId ?? ""),
    queryFn: () => listAttributeFieldOptions(fieldId!),
    enabled: fieldId !== null,
    staleTime: 10_000,
  });
}

function useInvalidateAfterOptionWrite(): (fieldId: string) => Promise<void> {
  const qc = useQueryClient();
  return async (fieldId: string) => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: attributeFieldKeys.options(fieldId) }),
      qc.invalidateQueries({ queryKey: ["admin", "attribute-fields", "list"] }),
    ]);
  };
}

export function useCreateAttributeFieldOption(): UseMutationResult<
  AttributeFieldOption,
  Error,
  { fieldId: string; body: AttributeFieldOptionCreate }
> {
  const invalidate = useInvalidateAfterOptionWrite();
  return useMutation<
    AttributeFieldOption,
    Error,
    { fieldId: string; body: AttributeFieldOptionCreate }
  >({
    mutationFn: ({ fieldId, body }) => createAttributeFieldOption(fieldId, body),
    onSuccess: (_data, { fieldId }) => invalidate(fieldId),
  });
}

export function useUpdateAttributeFieldOption(): UseMutationResult<
  AttributeFieldOption,
  Error,
  { fieldId: string; optionId: string; body: AttributeFieldOptionUpdate }
> {
  const invalidate = useInvalidateAfterOptionWrite();
  return useMutation<
    AttributeFieldOption,
    Error,
    { fieldId: string; optionId: string; body: AttributeFieldOptionUpdate }
  >({
    mutationFn: ({ fieldId, optionId, body }) =>
      updateAttributeFieldOption(fieldId, optionId, body),
    onSuccess: (_data, { fieldId }) => invalidate(fieldId),
  });
}

export function useArchiveAttributeFieldOption(): UseMutationResult<
  AttributeFieldOption,
  Error,
  { fieldId: string; optionId: string }
> {
  const invalidate = useInvalidateAfterOptionWrite();
  return useMutation<
    AttributeFieldOption,
    Error,
    { fieldId: string; optionId: string }
  >({
    mutationFn: ({ fieldId, optionId }) =>
      archiveAttributeFieldOption(fieldId, optionId),
    onSuccess: (_data, { fieldId }) => invalidate(fieldId),
  });
}

export function useUnarchiveAttributeFieldOption(): UseMutationResult<
  AttributeFieldOption,
  Error,
  { fieldId: string; optionId: string }
> {
  const invalidate = useInvalidateAfterOptionWrite();
  return useMutation<
    AttributeFieldOption,
    Error,
    { fieldId: string; optionId: string }
  >({
    mutationFn: ({ fieldId, optionId }) =>
      unarchiveAttributeFieldOption(fieldId, optionId),
    onSuccess: (_data, { fieldId }) => invalidate(fieldId),
  });
}

// ---------------------------------------------------------------------
// Field groups
// ---------------------------------------------------------------------

export function useAttributeFieldGroups(): UseQueryResult<
  AttributeFieldGroupListResponse,
  Error
> {
  return useQuery<AttributeFieldGroupListResponse, Error>({
    queryKey: attributeFieldGroupKeys.list(),
    queryFn: () => listAttributeFieldGroups(),
    staleTime: 10_000,
  });
}

function useInvalidateAfterGroupWrite(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: attributeFieldGroupKeys.list() }),
      qc.invalidateQueries({ queryKey: ["admin", "attribute-fields", "list"] }),
    ]);
  };
}

export function useCreateAttributeFieldGroup(): UseMutationResult<
  AttributeFieldGroup,
  Error,
  AttributeFieldGroupCreate
> {
  const invalidate = useInvalidateAfterGroupWrite();
  return useMutation<AttributeFieldGroup, Error, AttributeFieldGroupCreate>({
    mutationFn: (body) => createAttributeFieldGroup(body),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateAttributeFieldGroup(): UseMutationResult<
  AttributeFieldGroup,
  Error,
  { id: string; body: AttributeFieldGroupUpdate }
> {
  const invalidate = useInvalidateAfterGroupWrite();
  return useMutation<
    AttributeFieldGroup,
    Error,
    { id: string; body: AttributeFieldGroupUpdate }
  >({
    mutationFn: ({ id, body }) => updateAttributeFieldGroup(id, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteAttributeFieldGroup(): UseMutationResult<
  void,
  Error,
  string
> {
  const invalidate = useInvalidateAfterGroupWrite();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteAttributeFieldGroup(id),
    onSuccess: () => invalidate(),
  });
}
